import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { useDispatch } from 'react-redux';
import {
  Button,
  ButtonGroup,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Modal,
  TextField,
  Typography
} from '@material-ui/core';
import {
  updateEventValidationStatus,
  updateEventAnnotatedParticipantGender,
  updateEventAnnotatedParticipantClothingType,
  toggleEventSpecialInterest
} from 'actions';
import { VALIDATION_EXCLUDED_REASONS } from 'common/constants';

const useStyles = makeStyles(theme => ({
  excludedReasonModalBackdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.72)',
    zIndex: 1499
  },
  excludedReasonModalCard: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 380,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%',
    backgroundColor: theme.palette.background.paper,
    zIndex: 1500
  },
  excludedReasonModalActions: {
    justifyContent: 'flex-end'
  },
  excludedReasonModalField: {
    marginTop: theme.spacing(2),
    width: '100%'
  },
  excludedReasonHelper: {
    marginTop: theme.spacing(1)
  },
  excludedReasonBelowButtons: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.4
  },
  attributeSelection: {
    '& .MuiSvgIcon-root': {
      width: '0.8em',
      height: '0.8em',
      marginLeft: theme.spacing(1)
    },
    '& .MuiButton-root': {
      fontSize: '0.8em',
      height: 28
    },
    marginBottom: theme.spacing(1)
  },
  selectedButton: {
    backgroundColor: colors.blueGrey[50],
    boxShadow: 'inset 0 0 4px #B0BEC5'
  }
}));

const EventAnnotations = props => {
  const {
    event,
    processing,
    hideGender,
    hideValidationStatus,
    hideClothingType,
    hideSpecialInterest,
    className
  } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const [excludedReasonModalOpen, setExcludedReasonModalOpen] = useState(false);

  useEffect(() => {
    setExcludedReasonModalOpen(false);
  }, [event.id]);

  const handleUpdateEventValidationStatus = (status, validationExcludedReason) => {
    dispatch(
      updateEventValidationStatus(event.id, status, validationExcludedReason)
    );
  };

  const handleExcludedReasonChosen = reason => {
    if (!reason) {
      return;
    }
    handleUpdateEventValidationStatus('SUSPECT', reason);
    setExcludedReasonModalOpen(false);
  };

  const handleCloseExcludedReasonModal = () => {
    setExcludedReasonModalOpen(false);
  };

  const handleUpdateEventParticipantGender = gender => {
    dispatch(
      updateEventAnnotatedParticipantGender(
        event.id,
        event.annotatedParticipantGender === gender ? null : gender
      )
    );
  };

  const handleUpdateEventParticipantClothingType = clothingType => {
    dispatch(
      updateEventAnnotatedParticipantClothingType(
        event.id,
        event.annotatedParticipantClothingType === clothingType
          ? null
          : clothingType
      )
    );
  };

  const handleToggleEventSpecialInterest = event => {
    dispatch(toggleEventSpecialInterest(event.id));
  };

  return (
    <div className={className}>
      {!hideValidationStatus && (
        <>
          <ButtonGroup className={classes.attributeSelection} fullWidth>
            <Button
              disabled={processing}
              onClick={() => {
                setExcludedReasonModalOpen(false);
                handleUpdateEventValidationStatus('PENDING');
              }}
              variant="outlined"
              className={
                event.validationStatus === 'PENDING' ? classes.selectedButton : ''
              }>
              Pending
            </Button>
            <Button
              disabled={processing}
              onClick={() => {
                if (event.validationExcludedReason) {
                  handleUpdateEventValidationStatus(
                    'SUSPECT',
                    event.validationExcludedReason
                  );
                } else {
                  setExcludedReasonModalOpen(true);
                }
              }}
              variant="outlined"
              className={
                event.validationStatus === 'SUSPECT' ? classes.selectedButton : ''
              }>
              Excluded
            </Button>
            <Button
              disabled={processing}
              onClick={() => {
                setExcludedReasonModalOpen(false);
                handleUpdateEventValidationStatus('VERIFIED');
              }}
              variant="outlined"
              className={
                event.validationStatus === 'VERIFIED'
                  ? classes.selectedButton
                  : ''
              }>
              Verified
            </Button>
          </ButtonGroup>
          {event.validationStatus === 'SUSPECT' &&
            event.validationExcludedReason && (
              <Typography
                className={classes.excludedReasonBelowButtons}
                color="textSecondary"
                component="div"
                variant="body2">
                <strong>Excluded reason:</strong>{' '}
                {VALIDATION_EXCLUDED_REASONS[event.validationExcludedReason] ||
                  event.validationExcludedReason}
              </Typography>
            )}
          <Modal
            BackdropProps={{
              classes: { root: classes.excludedReasonModalBackdrop }
            }}
            disableScrollLock
            open={excludedReasonModalOpen}
            style={{ zIndex: 1500 }}
            onClose={handleCloseExcludedReasonModal}>
            <Card className={classes.excludedReasonModalCard}>
              <CardHeader title="Excluded reason" />
              <Divider />
              <CardContent>
                <Typography
                  className={classes.excludedReasonHelper}
                  color="textSecondary"
                  variant="body2">
                  Choose a reason to mark this walk as excluded.
                </Typography>
                <TextField
                  key={event.id}
                  className={classes.excludedReasonModalField}
                  inputProps={{
                    'aria-label': 'Exclusion reason'
                  }}
                  margin="dense"
                  defaultValue=""
                  onChange={e => handleExcludedReasonChosen(e.target.value)}
                  select
                  SelectProps={{ native: true }}
                  variant="outlined">
                  <option disabled value="">
                    Select a reason
                  </option>
                  {Object.keys(VALIDATION_EXCLUDED_REASONS).map(key => (
                    <option key={key} value={key}>
                      {VALIDATION_EXCLUDED_REASONS[key]}
                    </option>
                  ))}
                </TextField>
              </CardContent>
              <CardActions className={classes.excludedReasonModalActions}>
                <Button
                  color="primary"
                  onClick={handleCloseExcludedReasonModal}
                  variant="contained">
                  Cancel
                </Button>
              </CardActions>
            </Card>
          </Modal>
        </>
      )}
      {!hideGender && (
        <ButtonGroup className={classes.attributeSelection} fullWidth>
          <Button
            disabled={processing}
            onClick={() => handleUpdateEventParticipantGender('MALE')}
            variant="outlined"
            className={
              event.annotatedParticipantGender === 'MALE'
                ? classes.selectedButton
                : ''
            }>
            Male
          </Button>
          <Button
            disabled={processing}
            onClick={() => handleUpdateEventParticipantGender('FEMALE')}
            variant="outlined"
            className={
              event.annotatedParticipantGender === 'FEMALE'
                ? classes.selectedButton
                : ''
            }>
            Female
          </Button>
        </ButtonGroup>
      )}
      {!hideClothingType && (
        <ButtonGroup className={classes.attributeSelection} fullWidth>
          <Button
            disabled={processing}
            onClick={() => handleUpdateEventParticipantClothingType('LIGHT')}
            variant="outlined"
            className={clsx(
              classes.clothingTypeButton,
              event.annotatedParticipantClothingType === 'LIGHT'
                ? classes.selectedButton
                : ''
            )}>
            Light Clothing
          </Button>
          <Button
            disabled={processing}
            onClick={() => handleUpdateEventParticipantClothingType('MEDIUM')}
            variant="outlined"
            className={clsx(
              classes.clothingTypeButton,
              event.annotatedParticipantClothingType === 'MEDIUM'
                ? classes.selectedButton
                : ''
            )}>
            Medium Clothing
          </Button>
          <Button
            disabled={processing}
            onClick={() => handleUpdateEventParticipantClothingType('HEAVY')}
            variant="outlined"
            className={clsx(
              classes.clothingTypeButton,
              event.annotatedParticipantClothingType === 'HEAVY'
                ? classes.selectedButton
                : ''
            )}>
            Heavy Clothing
          </Button>
        </ButtonGroup>
      )}
      {!hideSpecialInterest && (
        <ButtonGroup className={classes.attributeSelection} fullWidth>
          <Button
            disabled={processing}
            onClick={() => handleToggleEventSpecialInterest(event)}
            variant="outlined"
            className={event.specialInterest ? classes.selectedButton : ''}>
            Special Interest
          </Button>
        </ButtonGroup>
      )}
    </div>
  );
};

EventAnnotations.propTypes = {
  event: PropTypes.object
};

export default EventAnnotations;
